import React, {useCallback, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {DataTable} from "../../components/table/data-table";
import DynamicFilterMenu from "../../components/common/table/dynamic-filter-menu";
import ActionsMenu from "../../components/common/table/actions-menu";
import RemovePopup from "../../components/common/popup/remove-popup";
import {getCustomFormat} from "../../utils/timeUtils";
import {COMPANY} from "../../api/endpoints";
import {getPathParam, getSelectParam} from "../../utils/converter";
import {classNames} from "../../utils/Utils";
import CustomerCell from "../../components/common/table/customer-cell";
import NumberFormat from "react-number-format";
import {StatusBarIcon} from "../../components/common/alert/status-bar-icon";
import {AcquisitionBar} from "../../components/common/alert/acquisition-bar";
import UsersToolbar from "../../components/common/card/users-toolbar";
import UserInitial from "../../components/common/table/user-initial";
import AnimatedPing from "../../components/common/progress/animated-ping";
import {useDocumentList} from "./hooks/useDocumentList";
import DocumentCreatePopup from "./components/document-create-popup";
import {cloneDocument} from "./services/document-service";
import {hasPermissionFor} from "../../components/partials/restricted/base-permission-wrapper";
import {permissionGroup, permissionSpecific} from "../../constants/permissions";
import {resolveDocumentPermissionGroup, resolveDocumentSlugFromType} from "./constants";
import * as Endpoint from "../../routes/endpoints";

const DEFAULT_HEIGHT = "h-[calc(100svh-140px)]";
const DEFAULT_FILTER_HEIGHT = "h-[calc(100svh-140px)]";
const EMPTY_PERMISSIONS = Object.freeze({
    isSuperAdmin: false,
    isAdmin: false,
    permissionDict: Object.freeze({}),
    permissionSpecificDict: Object.freeze({}),
});

const checkDate = (value) => {
    if (!value) {
        return false;
    }
    const today = new Date();
    const target = new Date(value);
    const diff = today - target;
    const days = diff / (1000 * 3600 * 24);

    if (days >= 8) {
        return "red";
    }
    if (days >= 4) {
        return "";
    }
    return false;
};

const getCreatorName = (creator) => {
    if (!creator) {
        return "";
    }
    const parts = [creator.firstname, creator.lastname].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "";
};

const resolveCustomer = (document) => (
    document?.customer
    ?? document?.company_document?.connection
    ?? null
);

const formatDateValue = (value) => (value ? getCustomFormat(value, "DD/MM/YYYY") : "-");

const formatBooleanValue = (value, t) => {
    if (value === true) {
        return t("app.common.yes", {defaultValue: "Yes"});
    }
    if (value === false) {
        return t("app.common.no", {defaultValue: "No"});
    }
    return "-";
};

const stripHtml = (value) => {
    if (typeof value !== "string") {
        return value ?? "";
    }
    return value.replace(/<[^>]+>/g, "").trim();
};

const DEFAULT_TOTAL_SOURCES = [
    "totals",
    "document_total",
    "documentTotal",
    "document_totals",
    "documentTotals",
];

const DOCUMENT_TOTAL_PRIORITY_SOURCES = [
    "document_total",
    "documentTotals",
    "document_totals",
    "documentTotal",
    "totals",
];

const collectTotalNodes = (document, sourceKey) => {
    if (!document || typeof document !== "object") {
        return [];
    }

    const pathSegments = Array.isArray(sourceKey)
        ? sourceKey
        : String(sourceKey ?? "")
            .split(".")
            .map((segment) => segment.trim())
            .filter(Boolean);

    let target = document;
    for (const segment of pathSegments) {
        if (!target || typeof target !== "object") {
            return [];
        }
        target = target?.[segment];
    }

    if (!target || typeof target !== "object") {
        return [];
    }

    const nodes = [];
    if (target?.general && typeof target.general === "object") {
        nodes.push(target.general);
    }
    nodes.push(target);

    return nodes;
};

const parseNumericValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return null;
};

const resolveTotalValue = (document, key, options = {}) => {
    const {source = null} = options;

    const sources = Array.isArray(source)
        ? source
        : source !== null && source !== undefined
            ? [source]
            : DEFAULT_TOTAL_SOURCES;

    const visited = new Set();
    const candidates = [];

    sources.forEach((sourceKey) => {
        collectTotalNodes(document, sourceKey).forEach((node) => {
            if (node && typeof node === "object" && !visited.has(node)) {
                visited.add(node);
                candidates.push(node);
            }
        });
    });

    if (!source && document && typeof document === "object" && !visited.has(document)) {
        candidates.push(document);
    }

    for (const candidate of candidates) {
        const parsed = parseNumericValue(candidate?.[key]);
        if (parsed !== null) {
            return parsed;
        }
    }

    return parseNumericValue(document?.[key]);
};

const resolveDocumentTotal = (document) => {
    const candidates = [
        "total_discounted",
        "total_net_price",
        "total_unit_price",
        "total_purchase_price",
    ];

    for (const key of candidates) {
        const value = resolveTotalValue(document, key);
        if (value !== null) {
            return value;
        }
    }

    const fallback = parseNumericValue(document?.total);
    if (fallback !== null) {
        return fallback;
    }
    return 0;
};

const resolveCurrencyDisplay = (document) => {
    const currency = document?.currency ?? {};
    const symbol = currency?.currency_symbol ?? currency?.symbol ?? "";
    const code = currency?.code ?? "";
    return {
        symbol,
        code,
    };
};

const DocumentList = ({
    type,
    title,
    enableCreate,
    onCreate,
    onViewDocument,
    onEditDocument,
    enableDelete,
                          canEdit,
    onDeleteSuccess,
}) => {
    const {t} = useTranslation();
    const {isExporter} = useSelector((state) => state.user);
    const {permissions: companyPermissions} = useSelector((state) => state.userCompany ?? {});
    const resolvedPermissions = companyPermissions ?? EMPTY_PERMISSIONS;
    const documentPermissionGroup = useMemo(
        () => resolveDocumentPermissionGroup({type}) ?? permissionGroup.DOCUMENT,
        [type],
    );
    const isExporterView = isExporter || isExporter === null;
    const documentDetailBasePath = useMemo(() => {
        if (!type) {
            return null;
        }
        const slug = resolveDocumentSlugFromType(type);
        if (!slug) {
            return null;
        }
        return `${Endpoint.SALES + Endpoint.DOCUMENTS_PAGE}/${slug}`;
    }, [type]);

    const {
        companyId,
        documents,
        totalRows,
        limit,
        offset,
        isLoading,
        isFetchingMore,
        dynamicFilters,
        availableSortColumns,
        defaultParams,
        activeDynamicFilter,
        lastCompanyType,
        handleSearch,
        handleSorting,
        handleDynamicFilterChange,
        handleFetchMore,
        refresh,
        removeDocument,
    } = useDocumentList({type});

    const [documentToRemove, setDocumentToRemove] = useState(null);
    const [isRemoveOpen, setIsRemoveOpen] = useState(false);
    const [cloneTarget, setCloneTarget] = useState(null);
    const [isCloneOpen, setIsCloneOpen] = useState(false);

    const canClonePermission = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_CLONE);
    const canClone = Boolean(companyId) && canClonePermission;
    const canEditAction = typeof onEditDocument === "function";
    const canRemoveAction = enableDelete && companyId;
    const hasActions = canClone || canEditAction || canRemoveAction;

    const canSortByKey = useCallback((sortKey) => {
        if (!sortKey) {
            return false;
        }
        if (!Array.isArray(availableSortColumns) || availableSortColumns.length === 0) {
            return false;
        }
        return availableSortColumns.includes(String(sortKey));
    }, [availableSortColumns]);

    const handleOpenClone = useCallback((document) => {
        setCloneTarget(document ?? null);
        setIsCloneOpen(true);
    }, []);

    const handleCloseClone = useCallback(() => {
        setCloneTarget(null);
        setIsCloneOpen(false);
    }, []);

    const handleOpenRemove = useCallback((document) => {
        setDocumentToRemove(document);
        setIsRemoveOpen(true);
    }, []);

    const handleCloseRemove = useCallback(() => {
        setIsRemoveOpen(false);
        setDocumentToRemove(null);
    }, []);

    const handleRemoveConfirmed = useCallback(() => {
        if (documentToRemove) {
            removeDocument(documentToRemove.id);
        }
        if (typeof onDeleteSuccess === "function" && documentToRemove) {
            onDeleteSuccess(documentToRemove);
        }
        refresh();
        handleCloseRemove();
    }, [documentToRemove, handleCloseRemove, onDeleteSuccess, refresh, removeDocument]);

    const handleCloneSuccess = useCallback((response) => {
        handleCloseClone();
        refresh();
        const createdId = response?.id ?? response?.document?.id ?? response?.document_id ?? null;
        if (createdId && typeof onViewDocument === "function") {
            onViewDocument({id: createdId});
        }
    }, [handleCloseClone, onViewDocument, refresh]);

    const cloneSubmitAction = useCallback(({companyId: submitCompanyId, type: submitType, data}) => {
        if (!cloneTarget?.id) {
            return Promise.reject(new Error(
                t("app.documents.clone_unavailable", {defaultValue: "Impossibile duplicare il documento selezionato"})
            ));
        }
        return cloneDocument({
            companyId: submitCompanyId,
            type: submitType,
            documentId: cloneTarget.id,
            data,
        });
    }, [cloneTarget, t]);

    const cloneInitialValues = useMemo(() => {
        if (!cloneTarget) {
            return null;
        }
        const connectionSource = cloneTarget.connection
            ?? cloneTarget.company_document?.connection
            ?? null;
        const connectionOptionRaw = connectionSource ? getSelectParam(connectionSource, "business_name") : null;
        const connectionOption = connectionOptionRaw && connectionOptionRaw.value ? connectionOptionRaw : null;

        const termsOptionRaw = cloneTarget?.terms_type ? getSelectParam(cloneTarget.terms_type, "name") : null;
        const termsOption = termsOptionRaw && termsOptionRaw.value ? termsOptionRaw : null;

        const currencyOptionRaw = cloneTarget?.currency ? getSelectParam(cloneTarget.currency, "code") : null;
        const currencyOption = currencyOptionRaw && currencyOptionRaw.value ? currencyOptionRaw : null;

        return {
            connection: connectionOption,
            expire_date: cloneTarget?.expire_date ?? "",
            terms_type: termsOption,
            delivery_date: cloneTarget?.delivery_date ?? "",
            currency: currencyOption,
            exchange_date: cloneTarget?.exchange_date ?? "",
            exchange_rate: cloneTarget?.exchange_rate ?? "",
        };
    }, [cloneTarget]);

    const cloneSelectedUsers = useMemo(() => {
        if (!Array.isArray(cloneTarget?.users)) {
            return [];
        }
        const unique = new Map();
        cloneTarget.users.forEach((item) => {
            const candidate = item?.user ?? item;
            if (candidate?.id && !unique.has(candidate.id)) {
                unique.set(candidate.id, candidate);
            }
        });
        return Array.from(unique.values());
    }, [cloneTarget]);

    const removeDetail = useMemo(() => {
        if (!documentToRemove || !companyId) {
            return null;
        }
        return {
            title: t("app.documents.remove_title", {defaultValue: "Rimuovi documento"}),
            message: t("app.documents.remove_description", {defaultValue: "Sei sicuro di voler eliminare questo documento?"}),
            endpoint: getPathParam([COMPANY, String(companyId), "header", type, documentToRemove.id]),
        };
    }, [companyId, documentToRemove, t, type]);

    const {columns, visibilityDefaults} = useMemo(() => {
        const registeredColumns = [];
        const visibilityDefaults = {};

        const registerColumn = (definition, {hidden = false, pin = null} = {}) => {
            if (!definition || !definition.id) {
                return;
            }
            if (registeredColumns.some((column) => column.id === definition.id)) {
                return;
            }
            const nextColumn = {...definition};
            const metaUpdates = {};

            const resolvedSortKey = nextColumn?.meta?.sortKey ?? nextColumn.id;

            if (nextColumn.enableSorting === undefined) {
                nextColumn.enableSorting = canSortByKey(resolvedSortKey);
            }

            if (nextColumn.enableHiding === undefined) {
                nextColumn.enableHiding = true;
            }

            if (typeof nextColumn.size === "number" && Number.isFinite(nextColumn.size)) {
                const resolvedSize = Math.max(nextColumn.size, 0);
                nextColumn.size = resolvedSize;
                if (nextColumn.minSize === undefined) {
                    nextColumn.minSize = resolvedSize;
                }
                if (nextColumn.meta?.minWidth === undefined) {
                    metaUpdates.minWidth = resolvedSize;
                }
            }

            if (pin) {
                metaUpdates.pin = pin;
            }

            if (Object.keys(metaUpdates).length > 0) {
                nextColumn.meta = {...nextColumn.meta, ...metaUpdates};
            }

            if (hidden && nextColumn.enableHiding !== false) {
                visibilityDefaults[nextColumn.id] = false;
            }
            registeredColumns.push(nextColumn);
        };

        const registerHiddenColumn = (definition) => {
            registerColumn(definition, {hidden: true});
        };

        const customerHeader = isExporterView
            ? t("app.common.customer", {defaultValue: "Customer"})
            : t("app.common.supplier", {defaultValue: "Supplier"});

        const customerColumn = {
            id: "customer",
            accessorFn: (row) => {
                const customer = resolveCustomer(row);
                return customer?.business_name ?? "-";
            },
            header: () => customerHeader,
            title: customerHeader,
            meta: {sortKey: "customer_business_name"},
            enableHiding: false,
            cell: ({row}) => {
                const record = row.original;
                const customer = resolveCustomer(record);
                const isInteractive = typeof onViewDocument === "function";
                const detailHref = documentDetailBasePath && record?.id
                    ? `${documentDetailBasePath}/${record.id}`
                    : null;

                const handleClick = (event) => {
                    if (!isInteractive) {
                        return;
                    }
                    if (event) {
                        if (event.defaultPrevented) {
                            return;
                        }
                        const isModifiedEvent = event.metaKey || event.ctrlKey || event.altKey || event.shiftKey;
                        const isPrimaryButton = event.button === undefined || event.button === 0;
                        if (isModifiedEvent || !isPrimaryButton) {
                            return;
                        }
                        event.preventDefault();
                    }
                    onViewDocument(record);
                };

                const content = (
                    <CustomerCell
                        customer={customer}
                    />
                );

                const interactiveClassName = classNames(
                    "flex w-full flex-col truncate text-left focus:outline-none",
                    isInteractive ? "cursor-pointer hover:text-gray-600" : "cursor-default"
                );

                return (
                    <div className="flex items-center space-x-3 lg:pl-2">
                        {isInteractive && detailHref ? (
                            <Link
                                to={detailHref}
                                onClick={handleClick}
                                className={interactiveClassName}
                            >
                                {content}
                            </Link>
                        ) : (
                            <button
                                type="button"
                                onClick={handleClick}
                                className={interactiveClassName}
                                disabled={!isInteractive}
                            >
                                {content}
                            </button>
                        )}
                    </div>
                );
            },
        };

        const internalReferenceColumn = {
            id: "internal_reference",
            accessorFn: (row) => row?.internal_reference ?? "",
            header: () => t("app.documents.internal_reference", {defaultValue: "Internal reference"}),
            title: t("app.documents.internal_reference", {defaultValue: "Internal reference"}),
            meta: {sortKey: "internal_reference"},
            enableHiding: false,
            cell: ({getValue}) => (
                <span className="text-sm text-gray-900">{getValue() || "-"}</span>
            ),
        };

        const priceColumn = {
            id: "price",
            accessorFn: (row) => resolveTotalValue(row, "total_net_price") ?? resolveDocumentTotal(row),
            header: () => t("app.quotations.price", {defaultValue: "Price"}),
            title: t("app.quotations.price", {defaultValue: "Price"}),
            meta: {sortKey: "total"},
            cell: ({row}) => {
                const record = row.original;
                const total = resolveTotalValue(record, "total_net_price");
                const fallbackTotal = total !== null ? total : resolveDocumentTotal(record);
                const {symbol, code} = resolveCurrencyDisplay(record);
                const currencyToken = symbol || code || "";
                const suffix = currencyToken ? ` ${currencyToken}` : "";

                return (
                    <div className="text-sm font-medium text-gray-900">
                        <NumberFormat
                            value={fallbackTotal}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            suffix={suffix}
                        />
                    </div>
                );
            },
        };

        const statusColumn = {
            id: "status",
            accessorKey: "status",
            header: () => t("app.common.status", {defaultValue: "Status"}),
            title: t("app.common.status", {defaultValue: "Status"}),
            cell: ({row}) => {
                const record = row.original;
                const status = (record?.status ?? "").toLowerCase();

                return (
                    <div className="flex flex-col gap-1">
                        <StatusBarIcon status={status} />
                    </div>
                );
            },
        };

        if (isExporterView) {
            const acquisitionColumn = {
                id: "acquisition_status",
                accessorFn: (row) => row?.acquisition_status ?? "",
                header: () => t("app.quotations.acquisition_status", {defaultValue: "Acquisition status"}),
                title: t("app.quotations.acquisition_status", {defaultValue: "Acquisition status"}),
                cell: ({row}) => {
                    const status = row.original?.acquisition_status;
                    if (!status) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    return <AcquisitionBar status={status} />;
                },
            };

            const usersColumn = {
                id: "users",
                accessorFn: (row) => (Array.isArray(row?.users) ? row.users.length : 0),
                header: () => t("app.users.users", {defaultValue: "Users"}),
                title: t("app.users.users", {defaultValue: "Users"}),
                cell: ({row}) => {
                    const element = row.original;
                    const users = Array.isArray(element?.users) ? element.users : [];
                    const extraDataDate = element?.quotation_date ?? element?.document_date;

                    if (users.length > 0) {
                        return (
                            <UsersToolbar
                                users={element?.users ?? []}
                                isActive={(element?.users ?? []).length > 1}
                                extraData={getCustomFormat(extraDataDate, "DD/MM/YYYY")}
                                className="relative"
                            >
                                <div className="flex items-center space-x-2">
                                    <div className={users.length === 1 ? "flex items-center gap-1 text-sm" : "flex flex-shrink-0 -space-x-1"}>
                                        {(users.length >= 4 ? users.slice(0, 4) : users).map((user, index) => (
                                            index === 0 ? (
                                                <UserInitial key={user?.id ?? index} user={user} ringClass=" ring-yellow-400 " />
                                            ) : (
                                                <UserInitial key={user?.id ?? index} user={user} />
                                            )
                                        ))}
                                        {users.length === 1 && (
                                            <div className="text-left ml-1">
                                                <div className="truncate text-sm text-gray-900">
                                                    {element.users[0]?.fullname ?? ""}
                                                </div>
                                                <div className="-mt-1 text-xs flex-wrap text-gray-500" style={{fontSize: "11px"}}>
                                                    {t("app.quotations.created_on", {
                                                        date: getCustomFormat(extraDataDate, "DD/MM/YYYY"),
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {users.length > (users.length >= 4 ? users.slice(0, 4) : users).length ? (
                                        <span className="flex-shrink-0 text-xs font-medium leading-5">
                                            +{users.length - (users.length >= 4 ? users.slice(0, 4) : users).length}
                                        </span>
                                    ) : null}
                                </div>
                            </UsersToolbar>
                        );
                    }

                    const creatorName = getCreatorName(element?.creator);
                    const dateValue = element?.quotation_date ?? element?.document_date;
                    if (!creatorName && !dateValue) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }

                    return (
                        <div className="flex flex-col text-sm text-gray-900">
                            {creatorName || "-"}
                            {dateValue && (
                                <span className="text-xs text-gray-500">
                                    {t("app.documents.document_date", {defaultValue: "Document"})}
                                    {": "}
                                    {getCustomFormat(dateValue, "DD/MM/YYYY")}
                                </span>
                            )}
                        </div>
                    );
                },
            };

            const lastContactColumn = {
                id: "last_contact",
                accessorFn: (row) => row?.last_contact ?? row?.next_reminder ?? row?.confirmed_date ?? row?.registration_date ?? "",
                header: () => t("app.quotations.last_contact", {defaultValue: "Last contact"}),
                title: t("app.quotations.last_contact", {defaultValue: "Last contact"}),
                cell: ({row}) => {
                    const record = row.original;
                    const rawDate = record?.last_contact
                        ?? record?.next_reminder
                        ?? record?.confirmed_date
                        ?? record?.registration_date;

                    if (!rawDate) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }

                    const pingColor = checkDate(rawDate);
                    return (
                        <div className="relative flex items-center pl-4">
                            {pingColor !== false && (
                                <AnimatedPing color={pingColor} extraClass="-ml-5" style={{marginTop: "4.1px"}} />
                            )}
                            <span className="text-sm text-gray-900">
                                {getCustomFormat(rawDate, "DD/MM/YYYY")}
                            </span>
                        </div>
                    );
                },
            };

            registerColumn(customerColumn, {pin: "left"});
            registerColumn(internalReferenceColumn);
            registerColumn(priceColumn);
            registerColumn(acquisitionColumn);
            registerColumn(statusColumn);
            registerColumn(usersColumn);
            registerColumn(lastContactColumn);
        } else {
            const expireColumn = {
                id: "expire_date",
                accessorFn: (row) => row?.expire_date ?? "",
                header: () => t("app.documents.expire_date", {defaultValue: "Expire date"}),
                title: t("app.documents.expire_date", {defaultValue: "Expire date"}),
                cell: ({row}) => {
                    const record = row.original;
                    const rawValue = row?.expire_date;
                    if (!rawValue) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    const formatted = getCustomFormat(rawValue, "DD/MM/YYYY");
                    const isExpired = new Date(rawValue) < new Date();
                    return (
                        <div className="flex flex-col">
                            <span
                                className={classNames(
                                    "text-sm font-medium",
                                    isExpired ? "text-red-600" : "text-gray-900"
                                )}
                            >
                                {formatted}
                            </span>
                            {record?.next_reminder && (
                                <span className="text-xs text-gray-500">
                                    {t("app.documents.next_reminder", {defaultValue: "Next reminder"})}
                                    {": "}
                                    {getCustomFormat(record.next_reminder, "DD/MM/YYYY")}
                                </span>
                            )}
                        </div>
                    );
                },
            };

            registerColumn(customerColumn, {pin: "left"});
            registerColumn(internalReferenceColumn);
            registerColumn(priceColumn);
            registerColumn(statusColumn);
            registerColumn(expireColumn);
        }

        if (hasActions) {
            registerColumn({
                id: "actions",
                header: "",
                size: 40,
                enableSorting: false,
                enableHiding: false,
                cell: ({row}) => {
                    const record = row.original;
                    const statusValue = String(record?.status ?? "").toLowerCase();
                    const isConfirmed = statusValue === "confirmed";

                    const actionItems = [];

                    if (canClone) {
                        actionItems.push(
                            <button
                                key="clone"
                                type="button"
                                className="text-left"
                                onClick={() => handleOpenClone(record)}
                            >
                                {t("app.common.clone", {defaultValue: "Duplica"})}
                            </button>,
                        );
                    }

                    if (canEdit && canEditAction ) {
                        actionItems.push(
                            <button
                                key="edit"
                                type="button"
                                className="text-left"
                                onClick={() => onEditDocument(record)}
                            >
                                {t("app.common.edit", {defaultValue: "Edit"})}
                            </button>,
                        );
                    }

                    if (canRemoveAction && !isConfirmed) {
                        actionItems.push(
                            <button
                                key="remove"
                                type="button"
                                className="text-left text-red-600"
                                onClick={() => handleOpenRemove(record)}
                            >
                                {t("app.common.remove", {defaultValue: "Remove"})}
                            </button>,
                        );
                    }

                    if (actionItems.length === 0) {
                        return null;
                    }

                    return (
                        <ActionsMenu>
                            {actionItems}
                        </ActionsMenu>
                    );
                },
            }, {pin: "right"});
        }

        const hiddenColumns = [
            {
                id: "registration_date",
                accessorFn: (row) => row?.registration_date ?? "",
                header: () => t("app.documents.registration_date", {defaultValue: "Registration date"}),
                title: t("app.documents.registration_date", {defaultValue: "Registration date"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{formatDateValue(getValue())}</span>
                ),
            },
            {
                id: "document_date",
                accessorFn: (row) => row?.document_date ?? "",
                header: () => t("app.documents.document_date", {defaultValue: "Document date"}),
                title: t("app.documents.document_date", {defaultValue: "Document date"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{formatDateValue(getValue())}</span>
                ),
            },
            {
                id: "delivery_date",
                accessorFn: (row) => row?.delivery_date ?? "",
                header: () => t("app.documents.delivery_date", {defaultValue: "Delivery date"}),
                title: t("app.documents.delivery_date", {defaultValue: "Delivery date"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{formatDateValue(getValue())}</span>
                ),
            },
            {
                id: "expire_date",
                accessorFn: (row) => row?.expire_date ?? "",
                header: () => t("app.documents.expire_date", {defaultValue: "Expire date"}),
                title: t("app.documents.expire_date", {defaultValue: "Expire date"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{formatDateValue(getValue())}</span>
                ),
            },
            {
                id: "exchange_date",
                accessorFn: (row) => row?.exchange_date ?? "",
                header: () => t("app.documents.exchange_date", {defaultValue: "Exchange date"}),
                title: t("app.documents.exchange_date", {defaultValue: "Exchange date"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{formatDateValue(getValue())}</span>
                ),
            },
            {
                id: "exchange_rate",
                accessorFn: (row) => row?.exchange_rate ?? null,
                header: () => t("app.documents.exchange_rate", {defaultValue: "Exchange rate"}),
                title: t("app.documents.exchange_rate", {defaultValue: "Exchange rate"}),
                cell: ({getValue}) => {
                    const value = getValue();
                    if (value === null || value === undefined || value === "") {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    const numeric = Number(value);
                    if (Number.isNaN(numeric)) {
                        return <span className="text-sm text-gray-900">{value}</span>;
                    }
                    return (
                        <NumberFormat
                            value={numeric}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={6}
                        />
                    );
                },
            },
            {
                id: "currency_code",
                accessorFn: (row) => row?.currency?.code ?? "",
                header: () => t("app.common.currency", {defaultValue: "Currency"}),
                title: t("app.common.currency", {defaultValue: "Currency"}),
                meta: {sortKey: "currency"},
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{getValue() || "-"}</span>
                ),
            },
            {
                id: "connection_can_view",
                accessorFn: (row) => row?.connection_can_view ?? null,
                header: () => t("app.documents.connection_can_view", {defaultValue: "Visible to customer"}),
                title: t("app.documents.connection_can_view", {defaultValue: "Visible to customer"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{formatBooleanValue(getValue(), t)}</span>
                ),
            },
            {
                id: "connection_note",
                accessorFn: (row) => stripHtml(row?.connection_note ?? ""),
                header: () => t("app.documents.connection_note", {defaultValue: "Customer note"}),
                title: t("app.documents.connection_note", {defaultValue: "Customer note"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{getValue() || "-"}</span>
                ),
            },
            {
                id: "internal_note",
                accessorFn: (row) => stripHtml(row?.internal_note ?? ""),
                header: () => t("app.documents.internal_note", {defaultValue: "Internal note"}),
                title: t("app.documents.internal_note", {defaultValue: "Internal note"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{getValue() || "-"}</span>
                ),
            },
            {
                id: "terms_type",
                accessorFn: (row) => row?.terms_type?.name ?? "",
                header: () => t("app.documents.terms_type", {defaultValue: "Incoterms"}),
                title: t("app.documents.terms_type", {defaultValue: "Incoterms"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{getValue() || "-"}</span>
                ),
            },
            {
                id: "confirmed_date",
                accessorFn: (row) => row?.confirmed_date ?? "",
                header: () => t("app.documents.confirmed_date", {defaultValue: "Confirmed date"}),
                title: t("app.documents.confirmed_date", {defaultValue: "Confirmed date"}),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{formatDateValue(getValue())}</span>
                ),
            },
            {
                id: "totals_total_purchase_price",
                accessorFn: (row) => resolveTotalValue(row, "total_purchase_price"),
                header: () => t("app.documents.total_purchase_price", {defaultValue: "Total purchase price"}),
                title: t("app.documents.total_purchase_price", {defaultValue: "Total purchase price"}),
                cell: ({row, getValue}) => {
                    const value = getValue();
                    if (value === null) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    const currency = row.original?.currency;
                    const suffix = currency?.currency_symbol || currency?.code || "";
                    return (
                        <NumberFormat
                            value={value}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            suffix={suffix ? ` ${suffix}` : ""}
                            className="text-sm text-gray-900"
                        />
                    );
                },
            },
            {
                id: "totals_total_unit_price",
                accessorFn: (row) => resolveTotalValue(row, "total_unit_price"),
                header: () => t("app.documents.total_unit_price", {defaultValue: "Total unit price"}),
                title: t("app.documents.total_unit_price", {defaultValue: "Total unit price"}),
                cell: ({row, getValue}) => {
                    const value = getValue();
                    if (value === null) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    const currency = row.original?.currency;
                    const suffix = currency?.currency_symbol || currency?.code || "";
                    return (
                        <NumberFormat
                            value={value}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            suffix={suffix ? ` ${suffix}` : ""}
                            className="text-sm text-gray-900"
                        />
                    );
                },
            },
            {
                id: "totals_total_discount",
                accessorFn: (row) => resolveTotalValue(row, "total_discount"),
                header: () => t("app.documents.total_discount", {defaultValue: "Total discount"}),
                title: t("app.documents.total_discount", {defaultValue: "Total discount"}),
                cell: ({row, getValue}) => {
                    const value = getValue();
                    if (value === null) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    const currency = row.original?.currency;
                    const suffix = currency?.currency_symbol || currency?.code || "";
                    return (
                        <NumberFormat
                            value={value}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            suffix={suffix ? ` ${suffix}` : ""}
                            className="text-sm text-gray-900"
                        />
                    );
                },
            },
            {
                id: "totals_total_rows",
                accessorFn: (row) => resolveTotalValue(row, "total_rows", {source: DOCUMENT_TOTAL_PRIORITY_SOURCES}),
                header: () => t("app.documents.total_row", {defaultValue: "Total rows"}),
                title: t("app.documents.total_row", {defaultValue: "Total rows"}),
                cell: ({getValue}) => {
                    const value = getValue();
                    if (value === null) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    return (
                        <NumberFormat
                            value={value}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={0}
                            className="text-sm text-gray-900"
                        />
                    );
                },
            },
            {
                id: "totals_total_volume",
                accessorFn: (row) => resolveTotalValue(row, "total_volume", {source: DOCUMENT_TOTAL_PRIORITY_SOURCES}),
                header: () => t("app.documents.metrics.total_volume", {defaultValue: "Total volume"}),
                title: t("app.documents.metrics.total_volume", {defaultValue: "Total volume"}),
                cell: ({getValue}) => {
                    const value = getValue();
                    if (value === null) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    return (
                        <NumberFormat
                            value={value}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={3}
                            fixedDecimalScale
                            className="text-sm text-gray-900"
                        />
                    );
                },
            },
            {
                id: "totals_total_net_weight",
                accessorFn: (row) => resolveTotalValue(row, "total_net_weight", {source: DOCUMENT_TOTAL_PRIORITY_SOURCES}),
                header: () => t("app.documents.metrics.total_net_weight", {defaultValue: "Total net weight"}),
                title: t("app.documents.metrics.total_net_weight", {defaultValue: "Total net weight"}),
                cell: ({getValue}) => {
                    const value = getValue();
                    if (value === null) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    return (
                        <NumberFormat
                            value={value}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={3}
                            fixedDecimalScale
                            className="text-sm text-gray-900"
                        />
                    );
                },
            },
            {
                id: "totals_total_gross_weight",
                accessorFn: (row) => resolveTotalValue(row, "total_gross_weight", {source: DOCUMENT_TOTAL_PRIORITY_SOURCES}),
                header: () => t("app.documents.metrics.total_gross_weight", {defaultValue: "Total gross weight"}),
                title: t("app.documents.metrics.total_gross_weight", {defaultValue: "Total gross weight"}),
                cell: ({getValue}) => {
                    const value = getValue();
                    if (value === null) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    return (
                        <NumberFormat
                            value={value}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={3}
                            fixedDecimalScale
                            className="text-sm text-gray-900"
                        />
                    );
                },
            },
            {
                id: "totals_total_box",
                accessorFn: (row) => resolveTotalValue(row, "total_box", {source: DOCUMENT_TOTAL_PRIORITY_SOURCES}),
                header: () => t("app.documents.metrics.total_box", {defaultValue: "Total boxes"}),
                title: t("app.documents.metrics.total_box", {defaultValue: "Total boxes"}),
                cell: ({getValue}) => {
                    const value = getValue();
                    if (value === null) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    return (
                        <NumberFormat
                            value={value}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={3}
                            fixedDecimalScale
                            className="text-sm text-gray-900"
                        />
                    );
                },
            },
        ];

        hiddenColumns.forEach(registerHiddenColumn);

        return {columns: registeredColumns, visibilityDefaults};
    }, [
        canClone,
        canEditAction,
        canRemoveAction,
        handleOpenClone,
        handleOpenRemove,
        hasActions,
        isExporterView,
        onEditDocument,
        onViewDocument,
        canSortByKey,
        canEdit,
        documentDetailBasePath,
        t,
    ]);

    const tableTitle = title ?? t("app.documents.documents", {defaultValue: "Documents"});

    return (
        <div className="space-y-4">
            <div className="shadow-sm">
                <DataTable
                    data={documents}
                    columns={columns}
                    total={totalRows}
                    offset={offset}
                    limit={limit}
                    isLoading={isLoading}
                    isFetchingMore={isFetchingMore}
                    onFetchingMore={handleFetchMore}
                    onSearch={handleSearch}
                    onSorting={handleSorting}
                    leftPinning={["customer"]}
                    rightPinning={hasActions ? ["actions"] : []}
                    paginationMode="infinite"
                    activePagination={false}
                    fixedHeight={DEFAULT_HEIGHT}
                    fixedHeightFilter={DEFAULT_FILTER_HEIGHT}
                    title={tableTitle}
                    totalRows={totalRows}
                    isDynamicSearch={true}
                    activeDynamicFilter={activeDynamicFilter}
                    listKey={`documents-${type}`}
                    enableColumnVisibility
                    enableColumnReorder
                    initialColumnVisibility={visibilityDefaults}
                >
                    <DynamicFilterMenu
                        filters={dynamicFilters || []}
                        defaultParams={defaultParams}
                        lastCompanyType={lastCompanyType}
                        isLoading={isLoading && documents.length === 0}
                        filterType={`${type}`}
                        onChange={handleDynamicFilterChange}
                    />
                </DataTable>
            </div>

            {removeDetail && (
                <RemovePopup
                    isOpen={isRemoveOpen}
                    setIsOpen={setIsRemoveOpen}
                    detail={removeDetail}
                    onRemoveConfirmed={handleRemoveConfirmed}
                />
            )}

            {canClone && (
                <DocumentCreatePopup
                    isOpen={isCloneOpen}
                    onClose={handleCloseClone}
                    type={type}
                    initialValues={cloneInitialValues}
                    selectedUsersDefault={cloneSelectedUsers}
                    submitAction={cloneSubmitAction}
                    modalTitle={t("app.documents.clone_document", {defaultValue: "Duplica documento"})}
                    submitLabel={t("app.common.clone", {defaultValue: "Duplica"})}
                    onSuccess={handleCloneSuccess}
                />
            )}
        </div>
    );
};

DocumentList.propTypes = {
    type: PropTypes.string.isRequired,
    title: PropTypes.string,
    enableCreate: PropTypes.bool,
    onCreate: PropTypes.func,
    onViewDocument: PropTypes.func,
    onEditDocument: PropTypes.func,
    enableDelete: PropTypes.bool,
    onDeleteSuccess: PropTypes.func,
};

DocumentList.defaultProps = {
    title: null,
    enableCreate: false,
    onCreate: undefined,
    onViewDocument: undefined,
    onEditDocument: undefined,
    enableDelete: true,
    onDeleteSuccess: undefined,
};

export default DocumentList;
